import { useEffect, useRef, useState } from "react";

/**
 * Reveals an element once it scrolls into view.
 *
 * The portfolio does this with a plain IntersectionObserver rather than a
 * library, and so does this. One observer per element is cheap at this page
 * size, and it keeps the reveal a property of the element that needs it instead
 * of something a parent has to orchestrate.
 *
 * Fires once and then disconnects: content that re-animates every time it
 * crosses the viewport is distracting on a page people scroll while reading a
 * reply.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(enabled = true) {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!enabled || shown) return;

    const node = ref.current;
    if (!node) return;

    // No IntersectionObserver (old browser, some test environments) means the
    // content must be shown, not hidden forever. Failing visible is the only
    // safe default for a progressive enhancement.
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      // A negative bottom margin holds the reveal until the element is properly
      // in view rather than one pixel past the fold, which is what makes the
      // movement feel intentional instead of twitchy.
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, shown]);

  return { ref, shown };
}
